import { Route } from "react-router";

import FeatureFlagWrapper from "@/components/UI/FeatureFlagWrapper";
import FeatureFlagBlock from "@/components/routing/FeatureFlagBlock";
import FeatureFlagGuard from "@/components/routing/FeatureFlagGuard";
import { RouteConfig } from "@/config/routes";

/**
 * Turns a `RouteConfig` tree into `<Route>` elements, enforcing each node's
 * feature flag on the route itself.
 *
 * Three properties are load-bearing, because a flag that is only applied to
 * the nav leaves the URL reachable by direct navigation:
 *
 * 1. A flag on a *layout* route (one with children but no component of its own,
 *    e.g. `logistics`, `risk`) gates the whole subtree. Previously such a flag
 *    was inert — the element was only built `if (component)` — so every child
 *    URL stayed reachable no matter what the section flag said.
 * 2. An ungranted flag *redirects* by default rather than rendering nothing.
 *    Rendering nothing leaves the shell with an empty content area, which reads
 *    as a broken screen rather than one the tenant is not entitled to.
 * 3. `blockedByFeatureFlag` is the inverse of `featureFlag`: the route is
 *    reachable *unless* the flag is granted. It is what makes "the links are
 *    gone" also mean "the URL is gone" — see `FeatureFlagBlock`. A route may
 *    carry both; the entitlement is checked first, so a tenant that is not
 *    entitled never reaches the blocking check.
 */
export function renderRoute(route: RouteConfig) {
  const {
    path,
    index,
    component: Comp,
    featureFlag,
    blockedByFeatureFlag,
    goToNotFound,
    wildcardChild,
    children,
  } = route;

  const key = path ?? (index ? "index" : "unknown");

  // Routes with no component and no children are metadata-only (e.g. AskVivo
  // config). Skip them so React Router doesn't render an empty leaf route.
  if (!Comp && !index && !children?.length) return null;

  // A route is a destination, so an ungranted flag must take the user
  // somewhere. Opt out explicitly with `goToNotFound: false`.
  const redirectWhenUngranted = goToNotFound ?? true;

  // A blocking flag wraps *outside* the entitlement check, so an ungranted
  // tenant still gets the entitlement's own redirect rather than this one.
  const withBlock = (node: React.ReactNode) =>
    blockedByFeatureFlag ? (
      <FeatureFlagBlock
        componentFlag={blockedByFeatureFlag}
        redirectToNotFound={redirectWhenUngranted}
      >
        {node}
      </FeatureFlagBlock>
    ) : (
      node
    );

  if (index) {
    const element = Comp ? <Comp /> : null;
    return (
      <Route
        key={key}
        index
        element={withBlock(
          featureFlag && element ? (
            <FeatureFlagWrapper
              componentFlag={featureFlag}
              goToNotFound={redirectWhenUngranted}
            >
              {element}
            </FeatureFlagWrapper>
          ) : (
            element
          ),
        )}
      />
    );
  }

  let element: React.ReactNode = null;
  if (Comp) {
    element = withBlock(
      featureFlag ? (
        <FeatureFlagWrapper
          componentFlag={featureFlag}
          goToNotFound={redirectWhenUngranted}
        >
          <Comp />
        </FeatureFlagWrapper>
      ) : (
        <Comp />
      ),
    );
  } else if (featureFlag) {
    // Layout route: gate the subtree. FeatureFlagGuard renders a bare
    // <Outlet /> when granted, so the DOM is unchanged for entitled tenants.
    element = withBlock(
      <FeatureFlagGuard
        componentFlag={featureFlag}
        redirectToNotFound={redirectWhenUngranted}
      />,
    );
  } else if (blockedByFeatureFlag) {
    // Layout route with only a blocking flag: block the whole subtree.
    element = (
      <FeatureFlagBlock
        componentFlag={blockedByFeatureFlag}
        redirectToNotFound={redirectWhenUngranted}
      />
    );
  }

  return (
    <Route key={key} path={path} element={element ?? undefined}>
      {wildcardChild && <Route path="*" element={null} />}
      {children?.map((child) => renderRoute(child))}
    </Route>
  );
}

export default renderRoute;
